/**
 * Supabase-native tester access hook
 * 
 * This provides a secure way to grant dev/testing access to specific users
 * without exposing dev tools to regular production users.
 * 
 * To grant tester access to a user:
 * 1. Go to Supabase Dashboard → Authentication → Users
 * 2. Find the user → Edit → Raw User Meta Data
 * 3. Add: { "is_tester": true }
 * 
 * Or via SQL:
 * UPDATE auth.users 
 * SET raw_user_meta_data = raw_user_meta_data || '{"is_tester": true}'::jsonb 
 * WHERE email = 'tester@example.com';
 */

import { useAuth } from '../AuthContext';

// Fallback tester emails (only used if user_metadata check fails)
// These should ideally be in Supabase, not hardcoded
const TESTER_EMAILS = [
  'jack@cannabias.co.uk',
  'admin@cannabias.co.uk',
  'test@cannabias.co.uk',
];

export function useTesterAccess() {
  const { user } = useAuth();
  
  // Check if user has tester access via Supabase user metadata
  const hasMetadataAccess = user?.user_metadata?.is_tester === true;
  
  // Check if user email is in the tester list (fallback)
  const hasEmailAccess = user?.email ? TESTER_EMAILS.includes(user.email) : false;
  
  // User has tester access if either condition is true
  const isTester = hasMetadataAccess || hasEmailAccess;
  
  return {
    isTester,
    // More granular access levels can be added here
    canAccessDevTools: isTester,
    canAccessDemoMode: isTester,
    canViewDebugPages: isTester,
  };
}

